// src/pages/Courses.tsx
import React from "react";
import AppShell from "../layout/AppShell";
import {
  adminListCourses,
  adminUpsertCourse,
  adminDeleteCourse,
} from "../services/adminApi";
import type {
  Course,
  CourseLevel,
  CourseVisibility,
  CoursePatch,
} from "../models/course";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  MdLibraryBooks,
  MdAdd,
  MdDelete,
  MdEdit,
  MdRefresh,
  MdClose,
  MdSave,
} from "react-icons/md";

import { COURSES_ROLES } from "../models/auth";
import { useAuthStore } from "../stores/authStore";

const LEVELS: readonly CourseLevel[] = ["beginner", "intermediate", "advanced"];
const VISIBILITY: readonly CourseVisibility[] = [
  "public",
  "private",
  "unlisted",
];

export default function Courses(): React.ReactElement {
  const [rows, setRows] = React.useState<Course[]>([]);
  const [cursor, setCursor] = React.useState<{
    createdAt: string;
    id: string;
  } | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [err, setErr] = React.useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [editing, setEditing] = React.useState<Course | null>(null);

  const canEdit = useAuthStore((s) => s.hasAnyRole(COURSES_ROLES));

  const load = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const params: {
          limit?: number;
          cursor?: { createdAt: string; id: string };
        } = { limit: 20 };
        if (!reset && cursor) params.cursor = cursor;
        const page = await adminListCourses(params);
        setRows((r) => (reset ? page.items : [...r, ...page.items]));
        setCursor(page.nextCursor);
        setErr(null);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [cursor]
  );

  React.useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (c: Course) => {
    setEditing(c);
    setDrawerOpen(true);
  };

  const onSave = async (courseId: string, patch: CoursePatch) => {
    await adminUpsertCourse(courseId, patch);
    await load(true);
    setDrawerOpen(false);
  };

  const onDelete = async (courseId: string) => {
    const proceed = window.confirm(
      "¿Eliminar curso? Esto no se puede deshacer."
    );
    if (!proceed) return;
    await adminDeleteCourse(courseId);
    await load(true);
  };

  return (
    <AppShell>
      <section className="card p-4">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <span className="icon-accent text-2xl">
              <MdLibraryBooks />
            </span>
            <h2 className="text-lg font-semibold">Cursos</h2>
            <span className="badge-tonal ml-2">{rows.length} registros</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn btn-outline flex items-center gap-1"
              onClick={() => load(true)}
              disabled={loading}
            >
              <MdRefresh /> Recargar
            </button>
            {canEdit && (
              <button
                className="btn btn-primary flex items-center gap-1"
                onClick={openCreate}
              >
                <MdAdd /> Nuevo
              </button>
            )}
          </div>
        </div>

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

        <div className="overflow-x-auto rounded-lg border border-surface">
          <table className="table-tonal text-sm">
            <thead>
              <tr>
                <Th>Título</Th>
                <Th>Nivel</Th>
                <Th>Visibilidad</Th>
                <Th>Tags</Th>
                <Th>Portada</Th>
                <Th>Enlace</Th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8">
                    <LoadingSpinner label="Cargando cursos…" />
                  </td>
                </tr>
              ) : (
                rows.map((c) => (
                  <tr key={c.id}>
                    <Td>{c.title}</Td>
                    <Td>{c.level ?? "—"}</Td>
                    <Td>{c.visibility ?? "—"}</Td>
                    <Td>
                      {Array.isArray(c.tags) && c.tags.length
                        ? c.tags.join(", ")
                        : "—"}
                    </Td>
                    <Td>
                      {typeof c.coverURL === "string" && c.coverURL.length ? (
                        <img
                          src={c.coverURL}
                          alt="cover"
                          className="h-10 w-16 rounded object-cover"
                        />
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td>
                      {typeof c.courseURL === "string" && c.courseURL.length ? (
                        <a
                          className="text-primary underline"
                          href={c.courseURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={c.courseURL}
                        >
                          Curso
                        </a>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        {canEdit && (
                          <>
                            <button
                              className="btn btn-outline flex items-center gap-1"
                              onClick={() => openEdit(c)}
                            >
                              <MdEdit /> Editar
                            </button>
                            <button
                              className="btn btn-outline flex items-center gap-1"
                              onClick={() => onDelete(c.id)}
                            >
                              <MdDelete /> Eliminar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-on-surface/70"
                  >
                    No hay cursos todavía. {canEdit ? "Crea el primero." : "—"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={loading || !cursor}
          >
            {loading ? "Cargando…" : cursor ? "Cargar más" : "No hay más"}
          </button>
        </div>
      </section>

      <CourseDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        editing={editing}
        onSave={onSave}
      />
    </AppShell>
  );
}

/* ───────── helpers UI ───────── */
function Th({ children }: { children: React.ReactNode }): React.ReactElement {
  return <th className="px-3 py-2">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }): React.ReactElement {
  return <td className="px-3 py-2 align-top">{children}</td>;
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <label className="block text-xs text-on-surface/70">{label}</label>
      <div>{children}</div>
    </div>
  );
}

/* ───────── drawer ───────── */
function CourseDrawer({
  open,
  onClose,
  editing,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  editing: Course | null;
  onSave: (courseId: string, patch: CoursePatch) => Promise<void>;
}): React.ReactElement | null {
  const [courseId, setCourseId] = React.useState<string>("");
  const [title, setTitle] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [coverURL, setCoverURL] = React.useState<string>("");
  const [courseURL, setCourseURL] = React.useState<string>("");
  const [level, setLevel] = React.useState<CourseLevel | "">("");
  const [visibility, setVisibility] = React.useState<CourseVisibility | "">("");
  const [tags, setTags] = React.useState<string>("");

  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setCourseId(editing.id);
      setTitle(editing.title);
      setDescription(editing.description ?? "");
      setCoverURL(typeof editing.coverURL === "string" ? editing.coverURL : "");
      setCourseURL(
        typeof editing.courseURL === "string" ? editing.courseURL : ""
      );
      setLevel(editing.level ?? "");
      setVisibility(editing.visibility ?? "");
      setTags(Array.isArray(editing.tags) ? editing.tags.join(", ") : "");
    } else {
      setCourseId("");
      setTitle("");
      setDescription("");
      setCoverURL("");
      setCourseURL("");
      setLevel("");
      setVisibility("");
      setTags("");
    }
    setError(null);
  }, [open, editing]);

  if (!open) return null;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim().length) {
      setError("Título requerido");
      return;
    }
    const url = courseURL.trim();
    if (url && !/^https?:\/\/.+/i.test(url)) {
      setError("El enlace debe comenzar con http(s)://");
      return;
    }

    const id =
      (editing ? editing.id : courseId.trim()) ||
      title.trim().toLowerCase().replace(/\s+/g, "-");
    const patch: CoursePatch = {};
    patch.title = title.trim();
    if (description.trim().length) patch.description = description.trim();
    if (coverURL.trim().length) patch.coverURL = coverURL.trim();
    if (url.length) patch.courseURL = url;
    if (level !== "") patch.level = level;
    if (visibility !== "") patch.visibility = visibility;
    const tagList = tags
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (tagList.length) patch.tags = tagList;

    try {
      setSaving(true);
      await onSave(id, patch);
    } catch (e2) {
      setError((e2 as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md overflow-y-auto bg-surface p-4 shadow-xl">
        <form onSubmit={submit} className="flex h-full flex-col">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {editing ? "Editar curso" : "Nuevo curso"}
            </h3>
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
              aria-label="Cerrar"
            >
              <MdClose />
            </button>
          </div>

          <div className="grow space-y-3">
            {!editing && (
              <Field label="ID del curso (opcional)">
                <input
                  className="input-surface"
                  value={courseId}
                  onChange={(e) => setCourseId(e.currentTarget.value)}
                  placeholder="si lo dejas vacío, se genera desde el título"
                />
              </Field>
            )}

            <Field label="Título *">
              <input
                className="input-surface"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
                required
              />
            </Field>

            <Field label="Descripción">
              <textarea
                className="input-surface h-24"
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Nivel">
                <select
                  className="input-surface"
                  value={level}
                  onChange={(e) =>
                    setLevel((e.target.value || "") as CourseLevel | "")
                  }
                >
                  <option value="">(sin nivel)</option>
                  {LEVELS.map((lv) => (
                    <option key={lv} value={lv}>
                      {lv}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Visibilidad">
                <select
                  className="input-surface"
                  value={visibility}
                  onChange={(e) =>
                    setVisibility(
                      (e.target.value || "") as CourseVisibility | ""
                    )
                  }
                >
                  <option value="">(sin visibilidad)</option>
                  {VISIBILITY.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Tags (separadas por coma)">
              <input
                className="input-surface"
                value={tags}
                onChange={(e) => setTags(e.currentTarget.value)}
                placeholder="ej. salud, nutrición, cardio"
              />
            </Field>

            <Field label="Portada (URL)">
              <input
                className="input-surface"
                value={coverURL}
                onChange={(e) => setCoverURL(e.currentTarget.value)}
                placeholder="https://…"
                inputMode="url"
              />
            </Field>

            <Field label="Enlace al curso (URL)">
              <input
                className="input-surface"
                value={courseURL}
                onChange={(e) => setCourseURL(e.currentTarget.value)}
                placeholder="https://…"
                inputMode="url"
                pattern="https?://.*"
                title="Debe comenzar con http(s)://"
              />
            </Field>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="sticky bottom-0 mt-3 flex items-center gap-2 border-t border-surface bg-surface py-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <MdSave /> {saving ? "Guardando…" : "Guardar"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
            >
              <MdClose /> Cancelar
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
